import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * Bundle Discount Component
 * Handles automatic bundle discounts when specific product combinations are in cart
 */
export class BundleDiscountComponent extends Component {
  requiredRefs = ['bundleDiscountMessage'];

  /** @type {Object} */
  #bundleConfig = {
    discountPercentage: 35, // Default bundle discount percentage
    bundleProducts: [] // Array of bundle product combinations
  };

  /** @type {boolean} */
  #isProcessing = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initializeBundleDiscount();
    this.#bindEvents();
  }

  /**
   * Initialize bundle discount configuration
   */
  #initializeBundleDiscount() {
    // Get bundle discount percentage from shop metafield or use default
    const discountPercentage = parseInt(this.dataset.bundleDiscountPercentage) || 35;
    this.#bundleConfig.discountPercentage = discountPercentage;

    // Load bundle product configurations
    this.#loadBundleConfigurations();
  }

  /**
   * Load bundle product configurations from metafields or settings
   */
  async #loadBundleConfigurations() {
    try {
      // This would typically load from shop metafields or settings
      // For now, we'll use a default configuration
      this.#bundleConfig.bundleProducts = [
        // Example bundle configuration
        // {
        //   product1Id: '123456789',
        //   product2Id: '987654321',
        //   discountPercentage: 35
        // }
      ];
    } catch (error) {
      console.error('Error loading bundle configurations:', error);
    }
  }

  /**
   * Bind event listeners
   */
  #bindEvents() {
    // Listen for cart updates
    document.addEventListener(ThemeEvents.CART_ADD, this.#handleCartUpdate.bind(this));
    document.addEventListener(ThemeEvents.CART_CHANGE, this.#handleCartUpdate.bind(this));
    document.addEventListener(ThemeEvents.CART_UPDATE, this.#handleCartUpdate.bind(this));
  }

  /**
   * Handle cart updates
   * @param {CustomEvent} event - Cart update event
   */
  async #handleCartUpdate(event) {
    if (this.#isProcessing) return;

    try {
      this.#isProcessing = true;
      
      const cart = event.detail?.cart;
      if (!cart) return;

      // Check if cart contains bundle products
      const bundleDiscount = this.#calculateBundleDiscount(cart);
      
      if (bundleDiscount.shouldApply) {
        await this.#applyBundleDiscount(bundleDiscount);
      } else {
        await this.#removeBundleDiscount();
      }
    } catch (error) {
      console.error('Error handling cart update:', error);
    } finally {
      this.#isProcessing = false;
    }
  }

  /**
   * Calculate if bundle discount should be applied
   * @param {Object} cart - Cart data
   * @returns {Object} Bundle discount calculation result
   */
  #calculateBundleDiscount(cart) {
    const items = cart.items || [];
    
    // Check for bundle product combinations
    for (const bundleConfig of this.#bundleConfig.bundleProducts) {
      const product1InCart = items.find(item => item.product_id.toString() === bundleConfig.product1Id);
      const product2InCart = items.find(item => item.product_id.toString() === bundleConfig.product2Id);
      
      if (product1InCart && product2InCart) {
        // Both bundle products are in cart
        const totalQuantity = Math.min(product1InCart.quantity, product2InCart.quantity);
        const discountAmount = this.#calculateDiscountAmount(product1InCart, product2InCart, totalQuantity);
        
        return {
          shouldApply: true,
          bundleConfig,
          discountAmount,
          affectedItems: [product1InCart, product2InCart],
          quantity: totalQuantity
        };
      }
    }
    
    return { shouldApply: false };
  }

  /**
   * Calculate discount amount for bundle products
   * @param {Object} product1 - First product in cart
   * @param {Object} product2 - Second product in cart
   * @param {number} quantity - Bundle quantity
   * @returns {number} Discount amount in cents
   */
  #calculateDiscountAmount(product1, product2, quantity) {
    const product1Total = product1.final_line_price;
    const product2Total = product2.final_line_price;
    const totalBeforeDiscount = product1Total + product2Total;
    
    const discountPercentage = this.#bundleConfig.discountPercentage / 100;
    const discountAmount = totalBeforeDiscount * discountPercentage;
    
    return Math.round(discountAmount);
  }

  /**
   * Apply bundle discount to cart
   * @param {Object} bundleDiscount - Bundle discount data
   */
  async #applyBundleDiscount(bundleDiscount) {
    try {
      // Create discount note for bundle
      const discountNote = `Bundle Discount: ${this.#bundleConfig.discountPercentage}% off`;
      
      // Update cart with discount note
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note: discountNote
        })
      });

      if (!response.ok) {
        throw new Error('Failed to apply bundle discount');
      }

      // Show bundle discount message
      this.#showBundleDiscountMessage(bundleDiscount);
      
      // Trigger cart update event
      const cartData = await response.json();
      this.dispatchEvent(new CustomEvent(ThemeEvents.CART_UPDATE, {
        detail: { cart: cartData },
        bubbles: true
      }));

    } catch (error) {
      console.error('Error applying bundle discount:', error);
    }
  }

  /**
   * Remove bundle discount from cart
   */
  async #removeBundleDiscount() {
    try {
      // Remove discount note if it exists
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note: ''
        })
      });

      if (response.ok) {
        // Hide bundle discount message
        this.#hideBundleDiscountMessage();
        
        // Trigger cart update event
        const cartData = await response.json();
        this.dispatchEvent(new CustomEvent(ThemeEvents.CART_UPDATE, {
          detail: { cart: cartData },
          bubbles: true
        }));
      }
    } catch (error) {
      console.error('Error removing bundle discount:', error);
    }
  }

  /**
   * Show bundle discount message
   * @param {Object} bundleDiscount - Bundle discount data
   */
  #showBundleDiscountMessage(bundleDiscount) {
    if (this.refs.bundleDiscountMessage) {
      const discountAmount = this.#formatMoney(bundleDiscount.discountAmount);
      this.refs.bundleDiscountMessage.textContent = `Bundle Discount Applied: Save ${discountAmount}`;
      this.refs.bundleDiscountMessage.classList.remove('hidden');
    }
  }

  /**
   * Hide bundle discount message
   */
  #hideBundleDiscountMessage() {
    if (this.refs.bundleDiscountMessage) {
      this.refs.bundleDiscountMessage.classList.add('hidden');
    }
  }

  /**
   * Format money value
   * @param {number} amount - Amount in cents
   * @returns {string} Formatted money string
   */
  #formatMoney(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100);
  }

  /**
   * Get current cart data
   * @returns {Promise<Object>} Cart data
   */
  async #getCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }
    return await response.json();
  }
}

// Register the component
customElements.define('bundle-discount-component', BundleDiscountComponent);
